import {
    CommunicationPasswordObject,
} from '@libs/types/src/services/object-type/person/person-communication-password.object';
import { Injectable } from '@angular/core';
import { GridInput } from '@it2go/data-grid';
import {
    AssignPersonToFolderInput,
    CreateEpicrisisInput,
    CreatePersonContactInput,
    CreatePersonInput,
    DeleteEpicrisisInput,
    DeletePersonContactInput,
    EpicrisisGridObject,
    EpicrisisObject,
    GetPersonInput,
    MarkPersonContactAsPrimaryInput,
    OkObject,
    PersonContactGridObject,
    PersonContactObject,
    PersonGridItemObject,
    PersonGridObject,
    SetContactOrderInput,
    UnmarkPrimaryPersonContactInput,
    UpdateEpicrisisInput,
    UpdatePersonContactInput,
    UpdatePersonInput,
} from '@it2go/types';
import { filterFrag } from './types/filter.types';
import {
    carePlanFrag,
    epicrisisGridItemFrag,
    personFrag,
    personGridItemFrag,
    personInsuranceFrag,
} from './types/person.types';
import { GqlQueryOptions, GqlServiceV2 } from '../modules/content/service/gql.service-v2';
import {
    PersonInsuranceGridObject,
    PersonInsuranceObject,
} from './../../../../types/src/services/object-type/person/person-insurance.object';
import {
    PersonInsuranceCreateInput,
} from './../../../../types/src/services/input-type/person/person-insurance.create-input';
import {
    PersonInsuranceUpdateInput,
} from './../../../../types/src/services/input-type/person/person-insurance.update-input';
import { IdInput } from './../../../../types/src/common/input-type/id.input';
import { CarePlanGridObject } from '@libs/types/src/services/object-type/folder/care-plan.object';
import { CarePlanCreateInput } from '@libs/types/src/services/input-type/folder/care-plan.create-input';
import { CarePlanUpdateInput } from '@libs/types/src/services/input-type/folder/care-plan.update-input';
import { exportFrag } from './types/common.types';
import { FolderPrintObject } from '@libs/types/src/services/object-type/folder/folder-print-object';
import { BaseFolderExportInput } from '@libs/types/src/services/input-type/folder/base-folder.export-input';
import { epicrisisFrag } from './types/folder.types';
import { FolderAssignPersonObject } from '@libs/types/src/services/object-type/folder/folder.assign-person-object';

@Injectable({ providedIn: 'root' })
export class PersonGqlServiceV2 extends GqlServiceV2 {

    public getPersons(input: GridInput | null, fragment: string = personGridItemFrag, options: GqlQueryOptions = {}) {
        return this.query<PersonGridObject>(
            `
                query($input: GridInput) {
                    person {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input },
            options,
        );
    }

    createPerson(input: CreatePersonInput, frag = personFrag) {
        return this.mutate<PersonGridItemObject>(
            `
                mutation($input: CreatePersonInput!) {
                    person {
                        create(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    assignPersonService(input: AssignPersonToFolderInput) {
        return this.mutate<FolderAssignPersonObject>(
            `
                mutation($input: AssignPersonToFolderInput!) {
                    person {
                        assignService(input: $input) {
                            folderId
                        }
                    }
                }
            `,
            { input },
        );
    }

    updatePerson(input: UpdatePersonInput, frag = personFrag) {
        return this.mutate<PersonGridItemObject>(
            `
                mutation($input: UpdatePersonInput!) {
                    person {
                        update(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getPersonInsurances(input: GridInput | null, frag = personInsuranceFrag) {
        return this.query<PersonInsuranceGridObject>(
            `
                query($input: GridInput) {
                    personInsurance {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createPersonInsurance(input: PersonInsuranceCreateInput, frag = personInsuranceFrag) {
        return this.mutate<PersonInsuranceObject>(
            `
                mutation($input: PersonInsuranceCreateInput!) {
                    personInsurance {
                        create(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public updatePersonInsurance(input: PersonInsuranceUpdateInput, frag = personInsuranceFrag) {
        return this.mutate<PersonInsuranceObject>(
            `
                mutation($input: PersonInsuranceUpdateInput!) {
                    personInsurance {
                        update(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public deletePersonInsurance(input: IdInput, frag = 'ok') {
        return this.mutate<OkObject>(
            `
                mutation($input: IdInput!) {
                    personInsurance {
                        delete(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getPersonCommunicationPassword(input: GetPersonInput, frag = 'communikePassword') {
        return this.query<CommunicationPasswordObject>(
            `
                query($input: GetPersonInput!) {
                    person {
                        getCommunicationPassword(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getEpicrises(input: GridInput | null, frag: string = epicrisisGridItemFrag) {
        return this.query<EpicrisisGridObject>(
            `
                query($input: GridInput) {
                    epicrisis {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createEpicrisis(input: CreateEpicrisisInput, frag: string = epicrisisFrag) {
        return this.mutate<EpicrisisObject>(
            `
                mutation($input: CreateEpicrisisInput!) {
                    epicrisis {
                        create(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public updateEpicrisis(input: UpdateEpicrisisInput, frag: string = epicrisisFrag) {
        return this.mutate<OkObject>(
            `
                mutation($input: UpdateEpicrisisInput!) {
                    epicrisis {
                        update(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public deleteEpicrisis(input: DeleteEpicrisisInput, frag: string = 'ok') {
        return this.mutate<OkObject>(
            `
                mutation($input: DeleteEpicrisisInput!) {
                    epicrisis {
                        delete(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public exportEpicrisis(input: BaseFolderExportInput, frag: string = exportFrag) {
        return this.query<FolderPrintObject>(
            `
                query($input: BaseFolderExportInput!) {
                    epicrisis {
                        export(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getCarePlans(input: GridInput | null, frag: string = carePlanFrag) {
        return this.query<CarePlanGridObject>(
            `
                query($input: GridInput) {
                    carePlan {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createCarePlan(input: CarePlanCreateInput, frag: string = carePlanFrag) {
        return this.mutate<EpicrisisObject>(
            `
                mutation($input: CarePlanCreateInput!) {
                    carePlan {
                        create(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public updateCarePlan(input: CarePlanUpdateInput, frag: string = carePlanFrag) {
        return this.mutate<OkObject>(
            `
                mutation($input: CarePlanUpdateInput!) {
                    carePlan {
                        update(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public deleteCarePlan(input: IdInput, frag: string = 'ok') {
        return this.mutate<OkObject>(
            `
                mutation($input: IdInput!) {
                    carePlan {
                        delete(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public exportCarePlan(input: BaseFolderExportInput, frag: string = exportFrag) {
        return this.query<FolderPrintObject>(
            `
                query($input: BaseFolderExportInput!) {
                    carePlan {
                        export(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getPersonRestrictions(input: GridInput | null, fragment: string) {
        return this.getPersonContacts(input, fragment);
    }

    public getPersonContacts(input: GridInput | null, fragment: string) {
        return this.query<PersonContactGridObject>(
            `
                query($input: GridInput) {
                    personContact {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createPersonContact(input: CreatePersonContactInput, fragment = 'id'): Promise<PersonContactObject> {
        return this.mutate<PersonContactObject>(
            `
                mutation($input: CreatePersonContactInput!) {
                    personContact {
                        create(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    updatePersonContact(input: UpdatePersonContactInput, fragment = 'ok') {
        return this.mutate<OkObject>(
            `
                mutation($input: UpdatePersonContactInput!) {
                    personContact {
                        update(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public deletePersonContact(input: DeletePersonContactInput, fragment = 'ok') {
        return this.mutate<OkObject>(
            `
                mutation($input: DeletePersonContactInput!) {
                    personContact {
                        delete(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public markPersonContactAsPrimary(input: MarkPersonContactAsPrimaryInput, fragment = 'ok') {
        return this.mutate<OkObject>(
            `
                mutation($input: MarkPersonContactAsPrimaryInput!) {
                    personContact {
                        markAsPrimary(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public unmarkPrimaryPersonContact(input: UnmarkPrimaryPersonContactInput, fragment = 'ok') {
        return this.mutate<OkObject>(
            `
                mutation($input: UnmarkPrimaryPersonContactInput!) {
                    personContact {
                        unmarkPrimary(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    reorderPersonContactOrder(input: SetContactOrderInput, fragment = 'ok') {
        return this.mutate<OkObject>(
            `
                mutation($input: SetContactOrderInput!) {
                    personContact {
                        reorder(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }
}
